'use client';
import { useEffect, useState } from 'react';
import { supabaseBrowser } from '@/lib/supabaseBrowser';

export type SignalScoreState = {
  score: number | null;
  ratingsCount: number | null;
};

// Returns { score, ratingsCount } instead of a bare number, because the Seed
// pre-stage (ratings_count === 0) can't be detected from score alone -- a
// brand-new user and a 0-score user with a bunch of flagged responses would
// otherwise look identical. Both fields are null together (logged out) or
// numbers together (logged in) -- they're never in a mismatched state.
//
// NOTE: this assumes `profiles.ratings_count` already exists as a column
// (the handoff doc implies it's already tracked server-side, just not
// fetched here yet). If it doesn't exist yet, this select will error --
// let me know and I'll add the migration.
export function useSignalScore(): SignalScoreState {
  const [score, setScore] = useState<number | null>(null);
  const [ratingsCount, setRatingsCount] = useState<number | null>(null);

  useEffect(() => {
    const supabase = supabaseBrowser();
    let mounted = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    function unsubscribeChannel() {
      if (channel) {
        supabase.removeChannel(channel);
        channel = null;
      }
    }

    async function load() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        if (mounted) {
          setScore(null);
          setRatingsCount(null);
        }
        unsubscribeChannel();
        return;
      }

      const { data } = await supabase
        .from('profiles')
        .select('signal_score, ratings_count')
        .eq('user_id', user.id)
        .maybeSingle();

      if (mounted) {
        setScore(typeof data?.signal_score === 'number' ? data.signal_score : 0);
        setRatingsCount(typeof data?.ratings_count === 'number' ? data.ratings_count : 0);
      }

      // Same live-update pattern as before, just also picking up
      // ratings_count changes on the same row.
      unsubscribeChannel();
      channel = supabase
        .channel(`profile-signal-score-${user.id}`)
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `user_id=eq.${user.id}` },
          (payload) => {
            const newScore = Number((payload.new as any)?.signal_score);
            const newRatingsCount = Number((payload.new as any)?.ratings_count);
            if (mounted) {
              if (Number.isFinite(newScore)) setScore(newScore);
              if (Number.isFinite(newRatingsCount)) setRatingsCount(newRatingsCount);
            }
          }
        )
        .subscribe();
    }

    load();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setScore(null);
        setRatingsCount(null);
        unsubscribeChannel();
      } else {
        load();
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
      unsubscribeChannel();
    };
  }, []);

  return { score, ratingsCount };
}
